import type { TokenPrices } from "@src/components/DefuseSDK/features/account/utils/holdingsUtils"
import { getPriceForToken } from "@src/components/DefuseSDK/features/account/utils/holdingsUtils"
import type {
  TokenInfo,
  TokenValue,
} from "@src/components/DefuseSDK/types/base"
import { formatTokenValue } from "@src/components/DefuseSDK/utils/format"
import {
  adjustDecimals,
  getUnderlyingBaseTokenInfos,
} from "@src/components/DefuseSDK/utils/tokenUtils"

export function tokenHasSingleBaseAmount(params: {
  token: TokenInfo
  balances: Record<string, bigint>
  requestedAmount: TokenValue
}): boolean {
  return getUnderlyingBaseTokenInfos(params.token).some((baseToken) => {
    const balance = params.balances[baseToken.defuseAssetId]
    if (balance == null) return false

    const decimals = Math.max(
      baseToken.decimals,
      params.requestedAmount.decimals
    )
    const normalizedBalance = adjustDecimals(
      balance,
      baseToken.decimals,
      decimals
    )
    const normalizedRequestedAmount = adjustDecimals(
      params.requestedAmount.amount,
      params.requestedAmount.decimals,
      decimals
    )

    return normalizedBalance >= normalizedRequestedAmount
  })
}

export function getDestinationUsdValue(params: {
  destinationToken: TokenInfo
  requestedAmount: TokenValue
  tokenPrices: TokenPrices
}): number | null {
  const destinationPrice = getPriceForToken(
    params.destinationToken,
    params.tokenPrices
  )
  if (destinationPrice == null || destinationPrice <= 0) return null

  return (
    Number(
      formatTokenValue(
        params.requestedAmount.amount,
        params.requestedAmount.decimals
      )
    ) * destinationPrice
  )
}

export function tokenHasSingleBaseUsd(params: {
  token: TokenInfo
  balances: Record<string, bigint>
  tokenPrices: TokenPrices
  requiredUsd: number
}): boolean {
  return getUnderlyingBaseTokenInfos(params.token).some((baseToken) => {
    const balance = params.balances[baseToken.defuseAssetId]
    if (balance == null || balance <= 0n) return false

    const price = getPriceForToken(baseToken, params.tokenPrices)
    if (price == null || price <= 0) return false

    const usdValue = (Number(balance) / 10 ** baseToken.decimals) * price
    return usdValue >= params.requiredUsd
  })
}

export type FundingMatch =
  | { kind: "same_asset" }
  | { kind: "cross_asset"; token: TokenInfo }
  | { kind: "unknown" }

export function findSingleTokenFundingMatch(params: {
  destinationToken: TokenInfo
  requestedAmount: TokenValue
  candidateTokens: TokenInfo[]
  balances: Record<string, bigint>
  tokenPrices: TokenPrices
  usdBufferMultiplier?: number
}): FundingMatch | null {
  if (
    tokenHasSingleBaseAmount({
      token: params.destinationToken,
      balances: params.balances,
      requestedAmount: params.requestedAmount,
    })
  ) {
    return { kind: "same_asset" }
  }

  const destinationUsd = getDestinationUsdValue({
    destinationToken: params.destinationToken,
    requestedAmount: params.requestedAmount,
    tokenPrices: params.tokenPrices,
  })
  if (destinationUsd == null) return { kind: "unknown" }

  const requiredUsd = destinationUsd * (params.usdBufferMultiplier ?? 1)
  const token = params.candidateTokens.find((candidate) =>
    tokenHasSingleBaseUsd({
      token: candidate,
      balances: params.balances,
      tokenPrices: params.tokenPrices,
      requiredUsd,
    })
  )

  return token != null ? { kind: "cross_asset", token } : null
}
